"use client";

import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { FormField, FormItem } from "@/components/ui/form";

interface TypeStepProps {
  control: Control<any>;
}

export function TypeStep({ control }: TypeStepProps) {
  const { t } = useTranslation(["finances", "common"]);

  return (
    <div className="flex h-full flex-col justify-center px-6 py-8 max-sm:px-4 max-sm:py-6">
      {/* Header */}
      <div className="mb-8 text-center max-sm:mb-6">
        <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
          <Icon
            className="text-muted-foreground"
            height="1.75rem"
            icon="tabler:credit-card"
            width="1.75rem"
          />
          {t("finances:createPayment.steps.selectType")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
          {t("finances:createPayment.steps.introDesc")}
        </p>
      </div>

      {/* Type Selection */}
      <div className="flex justify-center">
        <div className="w-full max-w-3xl">
          <FormField
            control={control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {[
                    {
                      value: "lesson",
                      label: t("finances:createPayment.lesson"),
                      description: t("finances:createPayment.lessonDescription"),
                      icon: "tabler:book",
                    },
                    {
                      value: "other",
                      label: t("finances:paymentsHistory.other"),
                      description: t("finances:createPayment.otherDescription"),
                      icon: "tabler:receipt",
                    },
                  ].map((type) => (
                    <motion.button
                      key={type.value}
                      className={`group relative overflow-hidden rounded-xl p-6 text-left ${
                        field.value === type.value
                          ? "bg-accent-foreground shadow-lg"
                          : "bg-muted/20 hover:bg-muted/30 hover:shadow-md"
                      }`}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => field.onChange(type.value)}
                    >
                      <div className="flex flex-col items-center gap-4 text-center">
                        <motion.div
                          animate={{
                            scale: field.value === type.value ? 1.1 : 1,
                          }}
                          className={`flex h-16 w-16 items-center justify-center rounded-2xl ${
                            field.value === type.value ? "bg-muted/50" : "bg-muted/30"
                          }`}
                          transition={{ duration: 0.2 }}
                        >
                          <Icon
                            className={
                              field.value === type.value
                                ? "text-foreground"
                                : "text-accent-foreground"
                            }
                            height="2rem"
                            icon={type.icon}
                            width="2rem"
                          />
                        </motion.div>
                        <div className="space-y-2">
                          <h3
                            className={`text-lg font-semibold ${
                              field.value === type.value
                                ? "text-foreground"
                                : "text-accent-foreground"
                            }`}
                          >
                            {type.label}
                          </h3>
                          <p
                            className={`max-w-[200px] text-sm leading-relaxed ${
                              field.value === type.value
                                ? "text-foreground"
                                : "text-muted-foreground"
                            }`}
                          >
                            {type.description}
                          </p>
                        </div>
                      </div>

                      {/* Selection indicator */}
                      {field.value === type.value && (
                        <motion.div
                          animate={{ scale: 1, opacity: 1 }}
                          className="absolute top-4 right-4"
                          initial={{ scale: 0, opacity: 0 }}
                        >
                          <div className="bg-muted/50 flex h-6 w-6 items-center justify-center rounded-full">
                            <Icon
                              className="text-foreground"
                              height="1rem"
                              icon="tabler:check"
                              width="1rem"
                            />
                          </div>
                        </motion.div>
                      )}
                    </motion.button>
                  ))}
                </div>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
